import React, { useCallback, useEffect, useMemo, useState } from "react";
import useAssetRemoveBackground from "domains/assets/hooks/useAssetRemoveBackground";
import { useAssetRemoveBackgroundCu } from "domains/assets/hooks/useAssetRemoveBackgroundCu";
import { BoundingBoxImageClass } from "domains/canvas/enum/BoundingBoxImageClass";
import { classifyImageTransparency } from "domains/canvas/utils/classifyImageTransparency";
import formatDate from "domains/canvas/utils/formatDate";
import { useClipboardContext } from "domains/commons/contexts/ClipboardProvider";
import ModalLibraryAssetSelect from "domains/image/components/ModalLibraryAssetSelect";
import { useAssetUpload } from "domains/image/hooks/useAssetUpload";
import useImageUploadDragDrop from "domains/image/hooks/useImageUploadDragDrop";
import { loadBase64FromUrl } from "domains/image/methods/loadDataFromUrl";
import { ReferenceAsset } from "domains/inference/interfaces/InferenceGenerator";
import { useScenarioToast } from "domains/notification/hooks/useScenarioToast";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import CompatibleImageUploader, {
  handleBlurFocusOnUploadClick,
  ImageListType,
} from "domains/ui/components/CompatibleImageUploader";
import Icon from "domains/ui/components/Icon";
import MenuItem from "domains/ui/components/Menu/MenuItem";
import ModalCrop from "domains/ui/components/ModalCrop";
import {
  GetAssetsByAssetIdApiResponse,
  useLazyGetAssetsByAssetIdQuery,
} from "infra/api/generated/api";
import _ from "lodash";
import mime from "mime-types";
import { useIntl } from "react-intl";

import {
  Box,
  Center,
  Flex,
  Image as ChakraImage,
  Menu,
  MenuButton,
  MenuList,
  Portal,
  SimpleGrid,
  Skeleton,
  Spinner,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";

import {
  BackgroundWarning,
  SidebarSectionReferenceImageBaseProps,
} from "./SectionReferenceImageBase";

interface SidebarFragmentReferenceImagesProps {
  assets: ReferenceAsset[];
  setAssets: (assets: ReferenceAsset[]) => void;
  maxImages: number;
  removeBackgroundTrackingExtraParams: Record<string, unknown>;
  isPasteEnabled?: boolean;
  recommendedBackgroundTransparency?: "transparent" | "opaque";
  selectLibrary: SidebarSectionReferenceImageBaseProps["selectLibrary"];
  kind: SidebarSectionReferenceImageBaseProps["kind"];
}

export default function SidebarFragmentReferenceImages({
  assets,
  setAssets: __setAssets,
  maxImages,
  removeBackgroundTrackingExtraParams,
  isPasteEnabled = false,
  recommendedBackgroundTransparency,
  selectLibrary,
  kind = "image",
}: SidebarFragmentReferenceImagesProps) {
  const { selectedProject } = useTeamContext();
  const {
    isOpen: isSelectImageModalOpen,
    onOpen: onOpenSelectImageModal,
    onClose: onCloseSelectImageModal,
  } = useDisclosure();
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [imageIndexToCrop, setImageIndexToCrop] = useState<
    number | undefined
  >();
  const { uploadImage } = useAssetUpload();
  const { handleRemoveBackgroundAsset, isRemoveBackgroundLoading } =
    useAssetRemoveBackground();
  const { successToast } = useScenarioToast();
  const { addImagePasteListener, removeImagePasteListener } =
    useClipboardContext();

  const canAddImage = useMemo(
    () => assets.length < maxImages,
    [assets.length, maxImages]
  );

  const setAssets = useCallback(
    (assets: ReferenceAsset[]) => {
      // remove duplicates asset id except if it's empty
      const newAssets = [];
      for (const asset of assets) {
        if (asset.isLoading) {
          newAssets.push(asset);
          continue;
        }
        if (newAssets.some((newAsset) => newAsset.id === asset.id)) {
          continue;
        }
        newAssets.push(asset);
      }
      __setAssets(newAssets);
    },
    [__setAssets]
  );

  const removeAtIndex = useCallback(
    (index: number) => {
      const tmpAssets = [...assets];
      tmpAssets.splice(index, 1);
      setAssets(tmpAssets);
    },
    [assets, setAssets]
  );

  const setAtIndex = useCallback(
    (asset: ReferenceAsset, index: number) => {
      const tmpAssets = [...assets];
      tmpAssets[index] = asset;
      setAssets(tmpAssets);
    },
    [assets, setAssets]
  );

  const replaceImage = useCallback(
    async ({
      src,
      asset,
      index,
    }: {
      src: string;
      asset?: GetAssetsByAssetIdApiResponse["asset"];
      index: number;
    }) => {
      if (asset) {
        setAtIndex(
          {
            ...asset,
            isLoading: false,
          },
          index
        );
        return;
      }

      setAtIndex(
        {
          isLoading: true,
        } as ReferenceAsset,
        index
      );
      const uploadedAsset = await uploadImage({
        data: src,
        name: "style-image-" + formatDate(Date.now()),
        kind,
      });
      if (uploadedAsset) {
        setAtIndex(
          {
            ...uploadedAsset,
            isLoading: false,
          },
          index
        );
      } else {
        removeAtIndex(index);
      }
    },
    [removeAtIndex, setAtIndex, uploadImage, kind]
  );

  const addAssets = useCallback(
    async (
      items: (
        | {
            src: string;
            name: string;
          }
        | {
            asset: GetAssetsByAssetIdApiResponse["asset"];
          }
      )[]
    ) => {
      const ogAssets = assets;
      setAssets([
        ...ogAssets,
        ...Array(items.length).fill({
          isLoading: true,
        } as ReferenceAsset),
      ]);
      const newAssets = await Promise.all(
        items.map(async (item) => {
          if ("asset" in item) {
            return {
              ...item.asset,
              isLoading: false,
            };
          }

          const asset = await uploadImage({
            data: item.src,
            name: item.name,
            kind,
          });
          if (asset) {
            return {
              ...asset,
              isLoading: false,
            };
          }
        })
      );
      setAssets([
        ...ogAssets,
        ...(newAssets.filter((asset) => !!asset) as ReferenceAsset[]),
      ]);
    },
    [assets, setAssets, uploadImage, kind]
  );

  const onRemoveBackground = useCallback(
    async (index: number) => {
      const asset = assets.length > index ? assets[index] : undefined;
      if (!asset) {
        return;
      }
      setAtIndex(
        {
          ...asset,
          isLoading: true,
        },
        index
      );
      const newAsset = await handleRemoveBackgroundAsset({
        assetId: asset.id,
        trackingExtraParams: removeBackgroundTrackingExtraParams,
      });
      if (newAsset) {
        void replaceImage({
          src: newAsset.url,
          asset: newAsset,
          index,
        });
      } else {
        setAtIndex(
          {
            ...asset,
            isLoading: false,
          },
          index
        );
      }
    },
    [
      handleRemoveBackgroundAsset,
      assets,
      removeBackgroundTrackingExtraParams,
      replaceImage,
      setAtIndex,
    ]
  );

  useEffect(() => {
    void (async () => {
      if (assets.length > 0) {
        for (const [index, asset] of assets.entries()) {
          if (
            asset.ownerId === selectedProject.id ||
            asset.privacy === "public"
          ) {
            continue;
          }
          setAtIndex(
            {
              ...asset,
              isLoading: true,
            },
            index
          );
          const base64 = asset.url.startsWith("http")
            ? await loadBase64FromUrl(asset.url, kind)
            : asset.url;
          if (!base64) {
            continue;
          }
          const uploadedAsset = await uploadImage({
            data: base64,
            name:
              asset.metadata.name ??
              `${asset.id}-copy.${mime.extension(asset.mimeType)}`,
            kind,
          });
          if (uploadedAsset) {
            setAtIndex(
              {
                ...uploadedAsset,
                isLoading: false,
              },
              index
            );
          }
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject.id]);

  const [getAssetById] = useLazyGetAssetsByAssetIdQuery();
  const { isDraggingHover, dragFunctions, onDrop } = useImageUploadDragDrop({
    onImageDrop: async ({ assetId }) => {
      if (!assetId) return;
      const ogAssets = assets;
      setAssets([
        ...ogAssets,
        {
          isLoading: true,
        } as ReferenceAsset,
      ]);
      const getAssetData = await getAssetById({
        projectId: selectedProject.id,
        assetId,
      });
      if (getAssetData.data?.asset) {
        setAssets([
          ...ogAssets,
          {
            ...getAssetData.data.asset,
            isLoading: false,
          },
        ]);
      } else {
        void removeAtIndex(assets.length);
      }
    },
  });

  const handleUploadInputChange = useCallback(
    async (imageList: ImageListType) => {
      void addAssets(
        imageList
          .map((item) => ({
            src: item.dataURL ?? "",
            name: item.name ?? "",
          }))
          .filter((item) => !!item.src)
      );
      onCloseSelectImageModal();
    },
    [addAssets, onCloseSelectImageModal]
  );

  useEffect(() => {
    if (!isPasteEnabled) {
      return;
    }
    const onImagePastedCallback = async (images: string[]) => {
      if (images.length > 0) {
        await addAssets(
          images.map((src) => ({
            src,
            name: "pasted-image-" + formatDate(Date.now()),
          }))
        );
        successToast({
          title: `Image${images.length > 1 ? "s" : ""} imported from clipboard`,
        });
      }
    };

    addImagePasteListener(onImagePastedCallback);

    return () => {
      removeImagePasteListener(onImagePastedCallback);
    };
  }, [
    addImagePasteListener,
    addAssets,
    isPasteEnabled,
    removeImagePasteListener,
    successToast,
  ]);

  // ----------------------------------

  const [imageTransparencies, setImageTransparencies] = useState<
    Record<string, BoundingBoxImageClass>
  >({});
  useEffect(() => {
    if (!recommendedBackgroundTransparency) {
      if (Object.keys(imageTransparencies).length > 0) {
        setImageTransparencies({});
      }
      return;
    }
    void (async () => {
      const newImageTransparencies: typeof imageTransparencies = {};
      for (const asset of assets) {
        if (asset.isLoading) {
          continue;
        }
        const previousTransparency = imageTransparencies[asset.id];
        if (previousTransparency) {
          newImageTransparencies[asset.id] = previousTransparency;
          continue;
        }
        const transparency = await classifyImageTransparency(asset.url);
        newImageTransparencies[asset.id] = transparency;
      }
      if (!_.isEqual(newImageTransparencies, imageTransparencies)) {
        setImageTransparencies(newImageTransparencies);
      }
    })();
  }, [imageTransparencies, assets, recommendedBackgroundTransparency]);

  // ----------------------------------

  return (
    <CompatibleImageUploader
      value={[]}
      onChange={handleUploadInputChange}
      allowNonImageType={kind !== "image"}
      acceptType={kind === "video" ? ["mp4"] : undefined}
      maxNumber={maxImages - assets.length}
      multiple
    >
      {({ onImageUpload, dragProps: { onDrop: dragPropsOnDrop } }) => {
        return (
          <VStack pt="10px" spacing={2}>
            {/*this somehow fixes the display bug on safari*/}
            <Flex w="100%">
              <SimpleGrid w="100%" columns={3} spacing={2}>
                {assets.map((asset, index) => (
                  <Box
                    key={`style-image-${index}`}
                    pos="relative"
                    w="100%"
                    aspectRatio="1"
                  >
                    <Box
                      overflow="hidden"
                      w="100%"
                      h="100%"
                      borderWidth={1}
                      borderRadius="md"
                    >
                      <ChakraImage
                        w="100%"
                        h="100%"
                        objectFit="contain"
                        alt={`Style Image ${index + 1}`}
                        fallback={<Skeleton w="100%" h="100%" />}
                        src={asset.url}
                      />
                    </Box>

                    {!!recommendedBackgroundTransparency &&
                      !!imageTransparencies[asset.id] &&
                      (recommendedBackgroundTransparency === "transparent") ===
                        (imageTransparencies[asset.id] ===
                          BoundingBoxImageClass.ZeroTransparent) && (
                        <BackgroundWarning
                          size="sm"
                          recommendedBackgroundTransparency={
                            recommendedBackgroundTransparency
                          }
                        />
                      )}

                    <Box
                      pos="absolute"
                      top={0}
                      right={0}
                      transform={"translate(50%, -50%)"}
                    >
                      <Menu isLazy placement="bottom-end">
                        <Center
                          as={MenuButton}
                          w="20px"
                          h="20px"
                          bg="white"
                          borderWidth={1}
                          borderRadius="full"
                          pointerEvents={asset.isLoading ? "none" : "auto"}
                          bgColor="black"
                        >
                          {asset.isLoading ? (
                            <Spinner w="12px" h="12px" />
                          ) : (
                            <Icon id="Ui/Dots" w="12px" />
                          )}
                        </Center>
                        <Portal>
                          <MenuList zIndex="tooltip">
                            <MenuItem
                              text="Remove"
                              onClick={() => removeAtIndex(index)}
                            />
                            <MenuItem
                              text="Crop"
                              onClick={() => setImageIndexToCrop(index)}
                            />
                            {recommendedBackgroundTransparency !== "opaque" && (
                              <RemoveBackgroundButton
                                assetId={asset.id}
                                onClick={() => onRemoveBackground(index)}
                                isDisabled={isRemoveBackgroundLoading}
                              />
                            )}
                          </MenuList>
                        </Portal>
                      </Menu>
                    </Box>
                  </Box>
                ))}

                {canAddImage && (
                  <Center
                    onDrop={(e) => onDrop(e, dragPropsOnDrop)}
                    {...dragFunctions}
                    overflow="hidden"
                    w="100%"
                    borderWidth={1}
                    borderColor={isDraggingHover ? "primary.500" : undefined}
                    borderRadius="md"
                    _hover={{
                      opacity: 0.75,
                    }}
                    cursor="pointer"
                    aspectRatio="1"
                    onClick={onOpenSelectImageModal}
                  >
                    <Icon
                      id="Ui/Plus"
                      h="25%"
                      color={isDraggingHover ? "primary.500" : undefined}
                    />
                  </Center>
                )}
              </SimpleGrid>
            </Flex>

            <ModalLibraryAssetSelect
              isOpen={isSelectImageModalOpen}
              selectionMax={maxImages - assets.length}
              onClose={onCloseSelectImageModal}
              onMultiSelect={(selectedAssets) => {
                void addAssets(
                  selectedAssets
                    .splice(0, maxImages - assets.length)
                    .map((asset) => ({
                      asset: asset.meta,
                    }))
                );
                onCloseSelectImageModal();
              }}
              assetFilterTypeProps={selectLibrary?.assetFilterTypeProps}
              tabs={selectLibrary?.tabs}
              buttonDroppableUploadProps={{
                onPaste: (imageData) => {
                  void addAssets([
                    {
                      src: imageData,
                      name: "pasted-image-" + formatDate(Date.now()),
                    },
                  ]);
                },
                isLoading: isUploading,
                isDraggingHover,
                onClick: () =>
                  handleBlurFocusOnUploadClick(onImageUpload, setIsUploading),
                onDrop: (event) => onDrop(event, dragPropsOnDrop),
                type: kind,
                ...dragFunctions,
              }}
            />

            {imageIndexToCrop !== undefined && (
              <ModalCrop
                imageToCrop={{
                  assetId: assets[imageIndexToCrop].id,
                  src: assets[imageIndexToCrop].url,
                }}
                onSubmit={async (asset) => {
                  void replaceImage({
                    src: asset.url,
                    asset,
                    index: imageIndexToCrop,
                  });
                  setImageIndexToCrop(undefined);
                }}
                onCancel={() => setImageIndexToCrop(undefined)}
              />
            )}
          </VStack>
        );
      }}
    </CompatibleImageUploader>
  );
}

function RemoveBackgroundButton({
  assetId,
  onClick,
  isDisabled,
}: {
  assetId: string;
  onClick: () => void;
  isDisabled: boolean;
}) {
  const { formatMessage } = useIntl();
  const { cuCost, isCuLoading } = useAssetRemoveBackgroundCu({
    assetId,
  });

  return (
    <MenuItem
      onClick={onClick}
      text={formatMessage({
        id: "domains.inference.inferenceGenerator.removeBackground",
        defaultMessage: "Remove Background",
      })}
      cuCost={{
        cost: cuCost,
        isLoading: isCuLoading,
      }}
      isDisabled={isDisabled}
    />
  );
}
